import { RemindersOffBanner } from "@/components/RemindersOffBanner";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Save, Mail, Send, Crown, Calendar, Rocket, FileText, Download, Music, BookOpen, Wrench } from "lucide-react";
import ClubMeetingScheduler from "@/components/ClubMeetingScheduler";
import EmailConfigPanel from "@/components/EmailConfigPanel";
import BroadcastPanel from "@/components/BroadcastPanel";
import EditionsManager from "@/components/EditionsManager";
import PlatformsManager from "@/components/PlatformsManager";
import { KickoffPanel } from "@/components/KickoffPanel";
import { EntryAuditLog } from "@/components/EntryAuditLog";

function ResetLeaguePanel({ form, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      // 1) aplica a configuração das fases que está no formulário acima
      await api.put("/settings", {
        edition_name: form.edition_name,
        current_week: 1,
        start_date: form.start_date,
        end_date: null,
        prep_weeks: form.prep_weeks,
        competition_weeks: form.competition_weeks,
        celebration_weeks: form.celebration_weeks,
        weeks_total: (form.prep_weeks || 0) + (form.competition_weeks || 0) + (form.celebration_weeks || 0),
      });
      // 2) apaga a actividade e devolve todos a Observador
      const { data } = await api.post("/admin/reset-league", { confirm, reset_roster: true });
      const total = Object.values(data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Edição recomeçada — ${total} registos apagados, ${data.roster_reset} consultores a Observador`);
      setConfirm(""); setOpen(false);
      onDone?.();
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const total = (form.prep_weeks || 0) + (form.competition_weeks || 0) + (form.celebration_weeks || 0);

  return (
    <div className="kw-card p-6 max-w-3xl border-[#E50000]/40" data-testid="reset-league-panel">
      <div className="flex items-center gap-3 mb-4">
        <Wrench className="w-6 h-6 text-[#E50000]" />
        <div>
          <div className="kw-overline">Zona sensível</div>
          <h2 className="font-display text-2xl font-black uppercase">Recomeçar a edição</h2>
        </div>
      </div>
      <p className="text-sm text-[#A3A3A3]">
        Faz duas coisas de uma vez: <strong>aplica a configuração que está acima</strong> e apaga
        <strong> definitivamente</strong> toda a actividade registada — registos semanais e diários, cartões,
        MVPs, capitães e presenças — devolvendo os consultores a <strong>Observador</strong> (0 a coopetir).
        Perfis, MBTI, equipas, utilizadores e candidaturas <strong>mantêm-se</strong>.
      </p>
      <div className="mt-4 border border-[#222] p-4 text-xs text-[#A3A3A3]" data-testid="reset-league-preview">
        <div className="kw-overline mb-2">Vai ficar assim</div>
        {form.edition_name || "—"} · início <strong>{form.start_date || "—"}</strong> ·{" "}
        <strong>{total} semanas</strong> = {form.prep_weeks || 0} preparação + {form.competition_weeks || 0} coopetição + {form.celebration_weeks || 0} celebração
      </div>
      {!open ? (
        <button className="kw-btn-secondary mt-4" onClick={() => setOpen(true)} data-testid="reset-league-open">
          Quero recomeçar a edição
        </button>
      ) : (
        <div className="mt-4 space-y-3 border-t border-[#222] pt-4">
          <label className="kw-overline block">Escreve RECOMECAR para confirmar</label>
          <input className="kw-input" value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())}
            placeholder="RECOMECAR" data-testid="reset-league-confirm" />
          <div className="flex gap-3">
            <button className="kw-btn-primary" disabled={busy || confirm !== "RECOMECAR"} onClick={run}
              data-testid="reset-league-run">{busy ? "A aplicar…" : "Aplicar configuração e recomeçar"}</button>
            <button className="kw-btn-secondary" onClick={() => { setOpen(false); setConfirm(""); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState({ edition_name: "", weeks_total: 13, current_week: 1, start_date: "2026-10-12", end_date: "", prep_weeks: 2, competition_weeks: 10, celebration_weeks: 1 });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mvpSending, setMvpSending] = useState(false);
  const [kickoffSending, setKickoffSending] = useState(false);
  const [gpsWeeklySending, setGpsWeeklySending] = useState(false);
  const [gpsMonthlySending, setGpsMonthlySending] = useState(false);
  const [mentorDigestSending, setMentorDigestSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setForm({
        edition_name: r.data.edition_name || "",
        weeks_total: r.data.weeks_total || 10,
        current_week: r.data.current_week || 1,
        start_date: r.data.start_date || "",
        end_date: r.data.end_date || "",
        prep_weeks: r.data.prep_weeks ?? 0,
        competition_weeks: r.data.competition_weeks ?? (r.data.weeks_total || 10),
        celebration_weeks: r.data.celebration_weeks ?? 0,
      });
      setStatus(r.data.status);
    });
  }, []);

  const onStartChange = (v) => {
    // When admin changes start, keep weeks fixed and let end auto-recompute on save (end stored empty)
    setForm((f) => ({ ...f, start_date: v }));
  };


  const onPhaseChange = (key, v) => {
    const n = Math.max(0, Number(v) || 0);
    setForm((f) => {
      const next = { ...f, [key]: n, end_date: "" };
      next.weeks_total = Math.max(1, (next.prep_weeks || 0) + (next.competition_weeks || 0) + (next.celebration_weeks || 0));
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date) { toast.error("Define a data de início"); return; }
    if (!form.competition_weeks) { toast.error("A edição precisa de pelo menos 1 semana de coopetição"); return; }
    setSaving(true);
    try {
      const payload = {
        edition_name: form.edition_name,
        current_week: form.current_week,
        start_date: form.start_date,
        end_date: null,
        prep_weeks: form.prep_weeks,
        competition_weeks: form.competition_weeks,
        celebration_weeks: form.celebration_weeks,
        weeks_total: (form.prep_weeks || 0) + (form.competition_weeks || 0) + (form.celebration_weeks || 0),
      };
      const { data } = await api.put("/settings", payload);
      setForm({
        edition_name: data.edition_name,
        weeks_total: data.weeks_total,
        current_week: data.current_week,
        start_date: data.start_date,
        end_date: data.end_date || "",
        prep_weeks: data.prep_weeks ?? 0,
        competition_weeks: data.competition_weeks ?? data.weeks_total,
        celebration_weeks: data.celebration_weeks ?? 0,
      });
      setStatus(data.status);
      toast.success("Configuração guardada");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const statusBadge = {
    upcoming: { label: "Por iniciar", color: "#3B82F6" },
    active: { label: "Em curso", color: "#00FF88" },
    ended: { label: "Terminada", color: "#FF6B6B" },
  }[status] || { label: "—", color: "#737373" };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/email/test");
      toast.success(`Email de teste enviado para ${data.to}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setTesting(false);
  };

  const sendReminders = async () => {
    if (!window.confirm("Enviar lembretes a TODOS os consultores registados?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/email/reminder");
      toast.success(`Enviado: ${data.sent}/${data.total_consultants} · falhas: ${data.failed.length}`);
      if (data.failed.length) console.warn("Failed:", data.failed);
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const sendMvpCongrats = async () => {
    if (!window.confirm(`Enviar email de parabéns aos 4 MVPs da semana ${form.current_week}?`)) return;
    setMvpSending(true);
    try {
      const { data } = await api.post(`/email/mvp-congrats?week=${form.current_week}`);
      toast.success(`Parabéns enviados: ${data.sent}/${data.winners_count} MVPs · falhas: ${data.failed.length}`);
      if (data.failed.length) console.warn("Failed:", data.failed);
    } catch (err) { toast.error(formatApiError(err)); }
    setMvpSending(false);
  };

  const sendMondayKickoff = async () => {
    if (!window.confirm(
      `Enviar email "Semana ${form.current_week} abriu" a TODOS os consultores ativos AGORA? ` +
      `Este envio é independente do cron automático de segunda-feira 09:00.`
    )) return;
    setKickoffSending(true);
    try {
      const { data } = await api.post("/admin/kickoff/run?force=true");
      toast.success(`Kickoff enviado: ${data.sent} emails · falhas: ${data.failed_count}`);
      if (data.failed_count) console.warn("Kickoff failed:", data.failed);
    } catch (err) { toast.error(formatApiError(err)); }
    setKickoffSending(false);
  };

  const sendGpsWeekly = async () => {
    if (!window.confirm(
      `Disparar AGORA o email semanal do GPS a todos os consultores COM 4-1-1 preenchido ` +
      `e aos coaches (só com consultores que partilharam)? Bypass do gate Domingo 20:00.`
    )) return;
    setGpsWeeklySending(true);
    try {
      const { data } = await api.post("/admin/gps/weekly/run?force=true");
      const r = data.result || {};
      const c = r.consultant || {};
      const co = r.coach || {};
      toast.success(
        `Consultores: ${c.sent} enviados · ${c.skipped} saltados · ${c.failed} falhas. ` +
        `Secretários: ${co.sent} enviados · ${co.skipped} saltados · ${co.failed} falhas.`,
      );
    } catch (err) { toast.error(formatApiError(err)); }
    setGpsWeeklySending(false);
  };

  const sendMentorDigest = async () => {
    if (!window.confirm(
      `Disparar AGORA o Mentor Digest a todos os coaches com pelo menos 1 mentorando? ` +
      `Bypass do gate Domingo 20:30.`
    )) return;
    setMentorDigestSending(true);
    try {
      const { data } = await api.post("/admin/mentor-digest/run?force=true");
      const r = data.result || {};
      toast.success(`Secretários: ${r.sent} enviados · ${r.skipped} saltados · ${r.failed} falhas.`);
    } catch (err) { toast.error(formatApiError(err)); }
    setMentorDigestSending(false);
  };

  const sendGpsMonthly = async () => {
    if (!window.confirm(
      `Disparar AGORA o Relatório Mensal YTD (PDF anexo) a todos os consultores com GPS configurado ` +
      `e aos seus mentores? Bypass do gate dia 5, 09:00.`
    )) return;
    setGpsMonthlySending(true);
    try {
      const { data } = await api.post("/admin/gps/monthly-report/run?force=true");
      const r = data.result || {};
      const c = r.consultant || {};
      const m = r.mentor || {};
      toast.success(
        `Consultores: ${c.sent} · Mentores: ${m.sent} · Saltados: ${c.skipped + m.skipped} · Falhas: ${c.failed + m.failed}`,
      );
    } catch (err) { toast.error(formatApiError(err)); }
    setGpsMonthlySending(false);
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="settings-page">
      <div>
        <div className="kw-overline mb-2">Configuração</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">Liga</h1>
      </div>

      <form onSubmit={submit} className="kw-card p-6 space-y-5 max-w-3xl">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#E50000]" />
            <div>
              <div className="kw-overline">Edição & Datas</div>
              <h2 className="font-display text-xl font-black uppercase">Período da Coopetição</h2>
            </div>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border" style={{ borderColor: statusBadge.color, color: statusBadge.color }} data-testid="settings-status">
            {statusBadge.label}
          </span>
        </div>

        <div>
          <label className="kw-overline block mb-2">Nome da edição</label>
          <input className="kw-input" value={form.edition_name}
            onChange={(e) => setForm({ ...form, edition_name: e.target.value })} data-testid="settings-edition" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="kw-overline block mb-2">Data de início</label>
            <input type="date" className="kw-input" value={form.start_date || ""}
              onChange={(e) => onStartChange(e.target.value)} data-testid="settings-startdate" />
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
              A semana 1 arranca sempre na segunda-feira desta semana
            </div>
          </div>
          <div>
            <label className="kw-overline block mb-2">Data de fim (automática)</label>
            <input type="date" className="kw-input opacity-60" value={form.end_date || ""} readOnly
              data-testid="settings-enddate" />
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
              Calculada a partir das {form.weeks_total} semanas
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 pt-3 border-t border-[#222]">
          <div>
            <label className="kw-overline block mb-2">Semanas de preparação</label>
            <input type="number" min="0" className="kw-input" value={form.prep_weeks}
              onChange={(e) => onPhaseChange("prep_weeks", e.target.value)} data-testid="settings-prep-weeks" />
          </div>
          <div>
            <label className="kw-overline block mb-2 text-[#22C55E]">Semanas de coopetição</label>
            <input type="number" min="1" className="kw-input" value={form.competition_weeks}
              onChange={(e) => onPhaseChange("competition_weeks", e.target.value)} data-testid="settings-competition-weeks" />
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
              As únicas que pontuam
            </div>
          </div>
          <div>
            <label className="kw-overline block mb-2">Semana(s) de celebração</label>
            <input type="number" min="0" className="kw-input" value={form.celebration_weeks}
              onChange={(e) => onPhaseChange("celebration_weeks", e.target.value)} data-testid="settings-celebration-weeks" />
          </div>
          <div>
            <label className="kw-overline block mb-2">Total</label>
            <div className="kw-input flex items-center font-display text-xl font-black" data-testid="settings-weeks">
              {form.weeks_total} semanas
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="kw-overline block mb-2">Semana atual (manual)</label>
            <input type="number" min="1" max={form.weeks_total} className="kw-input" value={form.current_week}
              onChange={(e) => setForm({ ...form, current_week: Number(e.target.value) })} data-testid="settings-current" />
          </div>
          <div className="flex items-end md:col-span-2">
            <button className="kw-btn-primary w-full inline-flex items-center justify-center gap-2" disabled={saving} data-testid="settings-save">
              <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar"}
            </button>
          </div>
        </div>

        <p className="text-xs text-[#737373] border-t border-[#222] pt-3">
          A edição tem três fases: <strong>preparação</strong>, <strong>coopetição</strong> (as únicas semanas que contam para pontos, rankings e MVPs) e <strong>celebração</strong>. Fora do período da edição, a app entra em modo <strong>Por iniciar</strong> ou <strong>Terminada</strong>.
        </p>
      </form>

      <div className="max-w-3xl"><KickoffPanel /></div>

      <div className="max-w-3xl"><EntryAuditLog /></div>
      <ResetLeaguePanel form={form} onDone={() => setStatus(null)} />

      <EditionsManager />

      <BroadcastPanel />

      <AnnouncementEditor />

      <ClubMeetingScheduler club={null} weeksTotal={form.weeks_total} currentWeek={form.current_week} mode="admin" />

      <TrainingLibrary />

      <PlatformsManager />

      <EmailConfigPanel />

      <div className="kw-card p-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-6 h-6 text-[#E50000]" />
          <div>
            <div className="kw-overline">Comunicação</div>
            <h2 className="font-display text-2xl font-black uppercase">Lembretes por Email</h2>
          </div>
        </div>
        <p className="text-sm text-[#A3A3A3] mb-5">
          Envia um email personalizado a todos os consultores com a sua pontuação atual e link direto para reportar.
          Usar antes de fechar a semana.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={sendTest} disabled={testing} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="email-test-btn">
            <Send className="w-4 h-4" /> {testing ? "A enviar…" : "Enviar teste ao admin"}
          </button>
          <button onClick={sendReminders} disabled={sending} className="kw-btn-primary inline-flex items-center gap-2" data-testid="email-reminder-btn">
            <Send className="w-4 h-4" /> {sending ? "A enviar…" : "Enviar lembretes a TODOS os consultores"}
          </button>
          <button onClick={sendMvpCongrats} disabled={mvpSending} className="kw-btn-primary inline-flex items-center gap-2" data-testid="email-mvp-btn"
            style={{ background: "#22C55E" }}>
            <Crown className="w-4 h-4" /> {mvpSending ? "A enviar…" : `Parabéns aos MVPs da semana ${form.current_week}`}
          </button>
          <button onClick={sendMondayKickoff} disabled={kickoffSending} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="email-kickoff-btn"
            style={{ borderColor: "#E50000", color: "#E50000" }}>
            <Rocket className="w-4 h-4" /> {kickoffSending ? "A enviar…" : `Kickoff semana ${form.current_week} (forçar)`}
          </button>
          <button onClick={sendGpsWeekly} disabled={gpsWeeklySending} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="email-gps-weekly-btn"
            style={{ borderColor: "#EAB308", color: "#EAB308" }}>
            <Rocket className="w-4 h-4" /> {gpsWeeklySending ? "A enviar…" : "GPS Semanal (Dom 20h — forçar)"}
          </button>
          <button onClick={sendGpsMonthly} disabled={gpsMonthlySending} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="email-gps-monthly-btn"
            style={{ borderColor: "#22C55E", color: "#22C55E" }}>
            <Rocket className="w-4 h-4" /> {gpsMonthlySending ? "A enviar…" : "GPS Mensal + PDF (dia 5 — forçar)"}
          </button>
          <button onClick={sendMentorDigest} disabled={mentorDigestSending} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="email-mentor-digest-btn"
            style={{ borderColor: "#3B82F6", color: "#3B82F6" }}>
            <Rocket className="w-4 h-4" /> {mentorDigestSending ? "A enviar…" : "Mentor Digest (Dom 20h30 — forçar)"}
          </button>
        </div>
        <p className="text-xs text-[#737373] mt-4 font-mono">
          Remetente: lead@kwgrowthleague.com · domínio verificado via Resend.<br/>
          Kickoff automático: toda a segunda-feira às 09:00 (Lisboa) — não precisas de carregar no botão.<br/>
          GPS Semanal automático: todos os domingos às 20:00 (Lisboa) — consultores recebem o foco da semana + coaches recebem resumo dos consultores que partilharam.<br/>
          GPS Mensal automático: todo o dia 5 do mês às 09:00 (Lisboa) — consultores recebem relatório YTD com PDF anexo + mentores selecionados recebem versão coach.<br/>
          Mentor Digest automático: todos os domingos às 20:30 (Lisboa) — cada coach recebe snapshot dos mentorandos que o escolheram, com flags de atenção.
        </p>
      </div>

      <MaintenanceCard />
    </div>
  );
}

function MaintenanceCard() {
  const [gpsBusy, setGpsBusy] = useState(false);
  const [coachBusy, setCoachBusy] = useState(false);
  const [weeksBusy, setWeeksBusy] = useState(false);
  const [remindersOn, setRemindersOn] = useState(true);
  const [togglingReminders, setTogglingReminders] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      // Default true (backwards-compat) unless explicitly disabled
      setRemindersOn(r.data.meeting_reminders_enabled !== false);
    }).catch(() => {});
  }, []);

  const toggleMeetingReminders = async () => {
    const next = !remindersOn;
    if (!window.confirm(next
      ? "Ativar convocatórias automáticas de reunião (T-24h e T-2h)?"
      : "Desligar convocatórias automáticas de reunião? Nenhum email T-24h/T-2h será enviado enquanto estiver OFF."
    )) return;
    setTogglingReminders(true);
    try {
      // Fetch current settings first to preserve required fields (edition_name, current_week)
      const cur = (await api.get("/settings")).data;
      await api.put("/settings", {
        edition_name: cur.edition_name,
        current_week: cur.current_week,
        weeks_total: cur.weeks_total,
        start_date: cur.start_date,
        end_date: cur.end_date,
        meeting_reminders_enabled: next,
      });
      setRemindersOn(next);
      toast.success(next ? "Convocatórias ATIVAS." : "Convocatórias DESLIGADAS.");
    } catch (err) { toast.error(formatApiError(err)); }
    setTogglingReminders(false);
  };

  const reconcileGps = async () => {
    if (!window.confirm("Reconciliar todos os GPS (1-3-5 vs 4-1-1) desalinhados? Idempotente e seguro.")) return;
    setGpsBusy(true);
    try {
      const { data } = await api.post("/admin/gps/reconcile-goals");
      toast.success(`GPS reconciliados: ${data.pairs_reconciled}/${data.pairs_checked} pares corrigidos.`);
    } catch (err) { toast.error(formatApiError(err)); }
    setGpsBusy(false);
  };

  const backfillCoaches = async () => {
    if (!window.confirm("Atribuir secretário do clube MBTI a todos os consultores sem secretário recrutador? Idempotente.")) return;
    setCoachBusy(true);
    try {
      const { data } = await api.post("/admin/coaches-ranking/backfill");
      toast.success(`Secretários: ${data.updated} consultores actualizados · ${data.coaches_found} secretários encontrados.`);
    } catch (err) { toast.error(formatApiError(err)); }
    setCoachBusy(false);
  };

  const recalcWeeks = async () => {
    if (!window.confirm("Recalcular todas as semanas (Segunda→Domingo) das entries existentes? Idempotente.")) return;
    setWeeksBusy(true);
    try {
      const { data } = await api.post("/admin/weeks/recalculate");
      toast.success(`Semanas: ${data.daily_entries_reassigned} entries diárias reatribuídas · ${data.weekly_entries_recomputed} agregados semanais recomputados.`);
    } catch (err) { toast.error(formatApiError(err)); }
    setWeeksBusy(false);
  };

  return (
    <div className="kw-card p-6" data-testid="maintenance-card">
      <div className="flex items-center gap-3 mb-4">
        <Wrench className="w-5 h-5 text-[#EAB308]" />
        <h2 className="font-display text-2xl font-black uppercase">Manutenção</h2>
      </div>
      {!remindersOn && <RemindersOffBanner onFix={toggleMeetingReminders} />}
      <p className="text-sm text-[#A3A3A3] mb-5">
        Operações one-shot de reconciliação de dados. Seguras (idempotentes) — podes correr as vezes que quiseres.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={toggleMeetingReminders}
          disabled={togglingReminders}
          className="kw-btn-secondary inline-flex items-center gap-2"
          style={{ borderColor: remindersOn ? "#00FF88" : "#FF6B6B", color: remindersOn ? "#00FF88" : "#FF6B6B" }}
          data-testid="maintenance-meeting-reminders-toggle"
        >
          <Wrench className="w-4 h-4" />
          {togglingReminders
            ? "A guardar…"
            : remindersOn
              ? "Convocatórias reunião: ON"
              : "Convocatórias reunião: OFF"}
        </button>
        <button
          onClick={reconcileGps}
          disabled={gpsBusy}
          className="kw-btn-secondary inline-flex items-center gap-2"
          style={{ borderColor: "#EAB308", color: "#EAB308" }}
          data-testid="maintenance-gps-reconcile"
        >
          <Wrench className="w-4 h-4" /> {gpsBusy ? "A reconciliar…" : "Reconciliar GPS (1-3-5 ↔ 4-1-1)"}
        </button>
        <button
          onClick={backfillCoaches}
          disabled={coachBusy}
          className="kw-btn-secondary inline-flex items-center gap-2"
          style={{ borderColor: "#3B82F6", color: "#3B82F6" }}
          data-testid="maintenance-coaches-backfill"
        >
          <Wrench className="w-4 h-4" /> {coachBusy ? "A atribuir…" : "Backfill Secretário por clube MBTI"}
        </button>
        <button
          onClick={recalcWeeks}
          disabled={weeksBusy}
          className="kw-btn-secondary inline-flex items-center gap-2"
          style={{ borderColor: "#22C55E", color: "#22C55E" }}
          data-testid="maintenance-weeks-recalc"
        >
          <Wrench className="w-4 h-4" /> {weeksBusy ? "A recalcular…" : "Recalcular semanas (Seg→Dom)"}
        </button>
      </div>
      <p className="text-xs text-[#737373] mt-4 font-mono">
        <strong className="text-[#EAB308]">Reconciliar GPS</strong>: iguala big_goal_amount_eur (1-3-5) e annual_goal_amount_eur (4-1-1) usando o valor mais recente.<br/>
        <strong className="text-[#3B82F6]">Backfill Secretário</strong>: atribui secretário do clube ao consultor sem secretário recrutador (usa MBTI para determinar clube).<br/>
        <strong className="text-[#22C55E]">Recalcular semanas</strong>: re-tag week_number das daily_entries usando a regra Seg→Dom actual.
      </p>
    </div>
  );
}

function AnnouncementEditor() {
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/announcement").then((r) => {
      setEnabled(!!r.data?.enabled);
      setTitle(r.data?.title || "");
      setBody(r.data?.body || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/announcement", { enabled, title, body });
      toast.success(enabled ? "Anúncio publicado" : "Anúncio guardado (desativado)");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="kw-card p-6 max-w-3xl" data-testid="announcement-editor">
      <div className="flex items-center gap-3 mb-4">
        <Crown className="w-6 h-6 text-[#E50000]" />
        <div>
          <div className="kw-overline">Comunicação direta</div>
          <h2 className="font-display text-2xl font-black uppercase">Popup de Anúncio</h2>
        </div>
      </div>
      <p className="text-sm text-[#A3A3A3] mb-4">
        Mensagem que aparece a todos os utilizadores quando entram na app. Fica visível até cada utilizador clicar em &quot;OK, percebi&quot;
        — depois só reaparece quando atualizares o conteúdo. Desativa para a esconder de toda a gente.
      </p>
      <label className="inline-flex items-center gap-2 mb-4 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="announcement-enabled" />
        <span className="kw-overline">Ativo</span>
      </label>
      <div className="space-y-3">
        <div>
          <label className="kw-overline block mb-1">Título</label>
          <input className="kw-input" value={title} maxLength={120}
            placeholder="Ex: Beta aberta para todos os clubes!"
            onChange={(e) => setTitle(e.target.value)} data-testid="announcement-title-input" />
        </div>
        <div>
          <label className="kw-overline block mb-1">Mensagem</label>
          <textarea className="kw-input min-h-[100px]" value={body} maxLength={1000}
            placeholder="Texto da mensagem que aparece dentro do popup…"
            onChange={(e) => setBody(e.target.value)} data-testid="announcement-body-input" />
        </div>
      </div>
      <button onClick={save} disabled={saving} className="kw-btn-primary mt-4 inline-flex items-center gap-2" data-testid="announcement-save">
        <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar Anúncio"}
      </button>
    </div>
  );
}

const CLUB_COLORS = {
  Analistas: "#9333EA",
  Diplomatas: "#10B981",
  Sentinelas: "#3B82F6",
  Exploradores: "#F59E0B",
};

function TrainingLibrary() {
  const [data, setData] = useState(null);
  const [filterClub, setFilterClub] = useState("all");
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    api.get("/admin/training-library").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="kw-card p-6 text-center text-[#737373]">A carregar biblioteca…</div>;

  const filtered = data.rows.filter((r) => {
    if (filterClub !== "all" && r.club !== filterClub) return false;
    if (!showEmpty && !r.has_pdf && !r.has_audio) return false;
    return true;
  });

  const downloadUrl = (relative) => {
    if (!relative) return "#";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${relative}`;
  };

  return (
    <div className="kw-card p-6" data-testid="training-library">
      <div className="flex items-center gap-3 mb-4 border-b border-[#222] pb-4 flex-wrap">
        <BookOpen className="w-6 h-6 text-[#E50000]" />
        <div className="flex-1 min-w-0">
          <div className="kw-overline">Recursos · Secretários</div>
          <h2 className="font-display text-2xl font-black uppercase">Biblioteca de Treinos</h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            PDFs e áudios carregados pelos coaches em cada sessão de auto-treino dos 4 clubes. Clica para abrir em nova tab.
          </p>
        </div>
        <select className="kw-input !py-2 md:w-44" value={filterClub} onChange={(e) => setFilterClub(e.target.value)} data-testid="library-filter-club">
          <option value="all">Todos os clubes</option>
          {Object.keys(CLUB_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 cursor-pointer text-xs">
          <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} data-testid="library-show-empty" />
          <span className="kw-overline">Mostrar vazias</span>
        </label>
      </div>

      {/* Summary cards per club */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {data.summary.map((s) => (
          <div key={s.club} className="border border-[#222] p-3 text-center" style={{ borderColor: `${CLUB_COLORS[s.club]}30` }} data-testid={`library-summary-${s.club}`}>
            <div className="kw-overline" style={{ color: CLUB_COLORS[s.club] }}>{s.club}</div>
            <div className="font-display text-2xl font-black mt-1">{s.pdfs}<span className="text-sm text-[#737373]">/{s.total}</span></div>
            <div className="text-[10px] text-[#737373] uppercase tracking-widest">PDFs · {s.audios} áudios</div>
          </div>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm italic text-[#737373] text-center py-6">
          {showEmpty ? "Nenhuma sessão neste filtro." : "Nenhum recurso carregado neste filtro. Ativa \"Mostrar vazias\" para ver as sessões por preencher."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#222]">
              <tr className="kw-overline">
                <th className="p-3">Clube</th>
                <th className="p-3 w-12 text-center">#</th>
                <th className="p-3">Sessão</th>
                <th className="p-3 text-center">PDF</th>
                <th className="p-3 text-center">Áudio</th>
                <th className="p-3 text-right">Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.club}-${r.session_number}`} className="border-b border-[#1A1A1A] hover:bg-[#141414]" data-testid={`library-row-${r.club}-${r.session_number}`}>
                  <td className="p-3"><span className="font-bold text-xs uppercase tracking-widest" style={{ color: CLUB_COLORS[r.club] }}>{r.club}</span></td>
                  <td className="p-3 text-center font-display font-black">{r.session_number}</td>
                  <td className="p-3">
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-[10px] text-[#737373] uppercase tracking-widest">{r.book_reference}</div>
                  </td>
                  <td className="p-3 text-center">
                    {r.has_pdf ? (
                      <a href={downloadUrl(r.pdf_url) + (document.cookie.match(/access_token=([^;]+)/) ? `?auth=${encodeURIComponent(document.cookie.match(/access_token=([^;]+)/)[1])}` : "")}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#E50000] hover:underline" title={r.pdf_filename || "Abrir PDF"}
                        data-testid={`library-pdf-${r.club}-${r.session_number}`}>
                        <FileText className="w-4 h-4" /> <Download className="w-3 h-3" />
                      </a>
                    ) : <span className="text-[#737373]">—</span>}
                  </td>
                  <td className="p-3 text-center">
                    {r.has_audio ? (
                      <a href={downloadUrl(r.audio_url) + (document.cookie.match(/access_token=([^;]+)/) ? `?auth=${encodeURIComponent(document.cookie.match(/access_token=([^;]+)/)[1])}` : "")}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline" title="Ouvir áudio"
                        data-testid={`library-audio-${r.club}-${r.session_number}`}>
                        <Music className="w-4 h-4" /> <Download className="w-3 h-3" />
                      </a>
                    ) : <span className="text-[#737373]">—</span>}
                  </td>
                  <td className="p-3 text-right text-xs text-[#737373]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("pt-PT") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

